import { NetworkStatus } from "@apollo/client";
import { PropsWithChildren, SelectHTMLAttributes, useId, useMemo } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { SiteStage } from "react-migration/domains/sites/typings/SiteStage";
import clsx from "clsx";
import { keyBy } from "lodash";

type StageId = string;

export interface StageSelectProps {
  value?: StageId;
  onChange: (id: StageId) => void;
  stages: SiteStage[];
  loading: boolean;
  networkStatus: NetworkStatus;
  size?: "xs" | "sm";
}

const NO_SELECTION = "NO_SELECTION";

export function StageSelect({
  value = NO_SELECTION,
  onChange,
  stages,
  networkStatus,
  loading,
  size = "sm",
}: StageSelectProps) {
  const { t } = useTranslation();
  const id = useId();
  const stagesById = useMemo(() => stages && keyBy(stages, "_id"), [stages]);
  const hasNoStages = networkStatus === NetworkStatus.ready && !stages?.length;

  return (
    <div
      className="atlas-flex atlas-flex-col atlas-gap-y-1 atlas-grow atlas-overflow-hidden"
      data-testid="stage-select"
    >
      <Select
        id={id}
        value={value}
        size={size}
        disabled={loading || hasNoStages}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={NO_SELECTION} disabled hidden>
          {hasNoStages
            ? t("sites.save_site.save_site_dropdown.create_stage_prompt")
            : t("sites.save_site.save_site_dropdown.select_stage_prompt")}
        </option>
        {(stages || []).map(({ _id, name }) => (
          <option key={_id} value={_id}>
            {name} {stagesById[_id]?.hidden && t("sites.save_site.save_site_dropdown.hidden")}
          </option>
        ))}
      </Select>
    </div>
  );
}

enum SelectSize {
  SM = "sm",
  XS = "xs",
}

type SelectProps = PropsWithChildren<{ size?: `${SelectSize}` }> &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, "className" | "size">;

function Select({ children, size = SelectSize.SM, ...props }: SelectProps) {
  return (
    <div className="atlas-relative atlas-flex atlas-items-center atlas-pointer">
      <select
        data-testid="stage-select-dropdown"
        className={clsx(
          "atlas-appearance-none atlas-w-full atlas-max-w-full atlas-truncate atlas-pl-2 atlas-pr-8 atlas-py-1 atlas-border-neutral-300 atlas-text-content-primary atlas-rounded-md focus:atlas-border-border-action-focus",
          {
            "!atlas-bg-neutral-100": props.disabled,
            "atlas-text-sm atlas-h-8": size === SelectSize.SM,
            "atlas-text-xs atlas-h-7": size === SelectSize.XS,
          }
        )}
        {...props}
      >
        {children}
      </select>
      <i
        className={clsx(
          "icon-lt-arrow-down-s-line atlas-absolute atlas-right-2 atlas-pointer-events-none",
          {
            "atlas-text-neutral-500": props.disabled,
            "atlas-text-lg": size === SelectSize.SM,
            "atlas-text-md": size === SelectSize.XS,
          }
        )}
      />
    </div>
  );
}
